/**
 * Utility functions for handling logout operations
 */

import { clearAllPlaygroundChatHistory } from 'utils/playgroundChatHistory';

// Custom event name for logout synchronization across tabs
export const LOGOUT_EVENT = 'app-logout';

/**
 * Clears all application data from localStorage
 * This should be called during logout to ensure complete cleanup
 */
export const clearAllLocalStorage = () => {
  // Auth related items
  // localStorage.removeItem('auth_token');
  localStorage.removeItem('sec_auth_m_tk');
  localStorage.removeItem('AUTH_TOKEN');
  localStorage.removeItem('design');
  localStorage.removeItem('convKey');
  localStorage.removeItem('_un');
  localStorage.removeItem('user');

  // Profile related items
  localStorage.removeItem('profilePic');

  // Chat and conversation history
  localStorage.removeItem('chatHistory');

  // Usage metrics
  localStorage.removeItem('_sys_ui_metrics_v2');

  // UI preferences
  localStorage.removeItem('kb_current_page');
  localStorage.removeItem('kb_page_size');

  // Payment and subscription related
  localStorage.removeItem('stripe_payment_auth_token');
  localStorage.removeItem('stripe_session_id');
  localStorage.removeItem('subscription_update_trigger');
  
  // Upgrade prompt related
  localStorage.removeItem('free_user_upgrade_prompt_shown');

  // Playground (chatHistory_*) and agent panel (chat_history_*) local caches
  clearAllPlaygroundChatHistory();

  const chatHistoryKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('chat_history_')) {
      chatHistoryKeys.push(key);
    }
  }

  chatHistoryKeys.forEach((key) => localStorage.removeItem(key));

  // Clear chat session mapping
  localStorage.removeItem('latest_chat_id_by_session_id');
};

/**
 * Clears all application data from sessionStorage
 * This should be called during logout to ensure complete cleanup
 */
export const clearAllSessionStorage = () => {
  // Embed authentication data
  sessionStorage.removeItem('chatbot_embed_auth');
  sessionStorage.removeItem('chatbot_embed_session');

  // Payment related session data
  sessionStorage.removeItem('stripe_payment_auth_token');
  sessionStorage.removeItem('stripe_session_id');

  // Clear all session storage items that might contain user-specific data
  const sessionKeys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (
      key.startsWith('user_') ||
      key.startsWith('chat_') ||
      key.startsWith('session_') ||
      key.startsWith('embed_') ||
      key.startsWith('stripe_')
    )) {
      sessionKeys.push(key);
    }
  }

  // Remove all user-specific session keys
  sessionKeys.forEach(key => sessionStorage.removeItem(key));
};

/**
 * Broadcasts a logout event to all tabs
 * This should be called after successful logout to synchronize state across tabs
 */
export const broadcastLogout = () => {
  // Dispatch a custom event that other tabs can listen for
  window.dispatchEvent(new Event(LOGOUT_EVENT));
  
  // Also use localStorage event for older browsers
  // We set and immediately remove a value to trigger storage events in other tabs
  localStorage.setItem('logout-trigger', Date.now().toString());
  localStorage.removeItem('logout-trigger');
};

/**
 * Clears Redux store state and RTK Query cache
 * This should be called during logout to reset application state
 */
export const clearReduxState = () => {
  // Import store and API utilities dynamically to avoid circular dependencies
  Promise.all([
    import('../store'),
    import('../store/slices/apiSlice')
  ]).then(([{ default: store }, { resetApiCache }]) => {
    // Clear RTK Query cache using the proper utility function
    if (store.dispatch && resetApiCache) {
      resetApiCache(store.dispatch);
      console.log('Redux state and RTK Query cache cleared successfully');
    }
  }).catch(error => {
    console.warn('Failed to clear Redux state:', error);
  });
};

/**
 * Complete logout function that:
 * 1. Clears all localStorage items
 * 2. Clears all sessionStorage items
 * 3. Clears Redux store state and RTK Query cache
 * 4. Broadcasts the logout event to other tabs
 */
export const performFullLogout = () => {
  clearAllLocalStorage();
  clearAllSessionStorage();
  clearReduxState();
  broadcastLogout();
};

/**
 * Sets up a listener for logout events from other tabs
 * @param {Function} callback - Function to call when logout event is received
 * @returns {Function} - Cleanup function to remove the listeners
 */
export const setupLogoutListener = (callback) => {
  const handleLogoutEvent = () => {
    callback();
  };
  
  const handleStorageEvent = (e) => {
    if (e.key === 'logout-trigger') {
      callback();
    }
  };
  
  window.addEventListener(LOGOUT_EVENT, handleLogoutEvent);
  window.addEventListener('storage', handleStorageEvent);
  
  // Return a cleanup function
  return () => {
    window.removeEventListener(LOGOUT_EVENT, handleLogoutEvent);
    window.removeEventListener('storage', handleStorageEvent);
  };
};
